import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { capitalizeFirstLetter } from "../../utils/string_util";
/**
 * A flexible component for inline editing that can be used as a
 * controlled or uncontrolled component.
 *
 * - Uncontrolled: Manages its own state with an inline "Edit" button.
 * - Controlled: A parent passes an `isEditing` prop to manage its state.
 */
function EditableField({
  value: controlledValue,
  type,
  name,
  onSave,
  isEditing: isControlledEditing, // This prop makes it a controlled component
  options = [],
}) {
  // Determine if the component is controlled by checking if isControlledEditing is passed.
  const isControlled = typeof isControlledEditing === "boolean";

  // Internal state for the value during an edit session.
  const [internalValue, setInternalValue] = useState(controlledValue);

  // Internal state for editing mode, ONLY used when uncontrolled.
  const [uncontrolledIsEditing, setUncontrolledIsEditing] = useState(false);

  // Synchronize internal value with external prop changes.
  useEffect(() => {
    setInternalValue(controlledValue);
  }, [controlledValue]);

  // Decide which 'isEditing' state to use.
  const isEditing = isControlled ? isControlledEditing : uncontrolledIsEditing;

  // A single handler to update internal state and notify the parent if controlled.
  const handleChange = (e) => {
    const { name, value, type: elementType, checked } = e.target;
    const newValue = elementType === "checkbox" ? checked : value;

    setInternalValue(newValue);
    // In controlled mode, the parent state should be updated on every change.
    // The prop `onSave` acts like an `onChange` callback here.
    if (isControlled) {
      onSave({
        target: {
          name: name,
          value: newValue,
          type: type,
        },
      });
    }
  };

  const handleSave = () => {
    onSave(internalValue);
    if (!isControlled) {
      setUncontrolledIsEditing(false);
    }
  };

  const handleCancel = () => {
    setInternalValue(controlledValue); // Revert to the original value
    if (!isControlled) {
      setUncontrolledIsEditing(false);
    }
  };

  const handleEditClick = () => {
    setUncontrolledIsEditing(true);
  };

  const renderEditMode = () => {
    switch (type) {
      case "number":
        return (
          <input
            name={name}
            type="number"
            value={Number(internalValue)}
            // Use the new centralized handler
            onChange={(e) => handleChange(e)}
            autoFocus
            className="w-full max-w-xs px-3 py-2 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
          />
        );
      case "boolean":
        return (
          <select
            name={name}
            value={Boolean(internalValue)}
            onChange={(e) => handleChange(e)}
            autoFocus
            className="w-full max-w-xs px-3 py-2 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 cursor-pointer"
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );
      case "option":
        return (
          <select
            name={name}
            value={String(internalValue).toLowerCase()}
            // Use the new centralized handler
            onChange={(e) => handleChange(e)}
            autoFocus
            className="w-full max-w-xs px-3 py-2 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 cursor-pointer"
          >
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      default: // 'string'
        return (
          <input
            name={name}
            type="text"
            value={internalValue}
            // Use the new centralized handler
            onChange={(e) => handleChange(e)}
            autoFocus
            className="w-full max-w-xs px-3 py-2 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
          />
        );
    }
  };

  return (
    <div
      style={{
        gap: "10px",
        marginBottom: "1rem",
      }}
      className="text-lg text-gray-800 w-full flex"
    >
      {isEditing ? (
        <>
          {renderEditMode()}
          {!isControlled && (
            <>
              <button
                onClick={handleSave}
                className="px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Save
              </button>
              <button
                onClick={handleCancel}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
            </>
          )}
        </>
      ) : (
        <>
          <span style={{ minHeight: "22px" }}>
            {/* Display "Yes"/"No" for boolean values, otherwise the value itself */}
            {typeof controlledValue === "boolean"
              ? controlledValue
                ? "Yes"
                : "No"
              : capitalizeFirstLetter(String(controlledValue))}
          </span>
          {!isControlled && (
            <button onClick={handleEditClick} style={{ cursor: "pointer" }}>
              Edit
            </button>
          )}
        </>
      )}
    </div>
  );
}

EditableField.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]).isRequired,
  type: PropTypes.oneOf(["string", "number", "boolean", "option"]).isRequired,
  onSave: PropTypes.func.isRequired,
  label: PropTypes.string,
  isEditing: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
};

export default EditableField;
